import styles from './products-report.module.scss';
import classNames from 'classnames';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { ProductTrackingType } from '../../../clients/quipli-service/product/product';
import { Spinner } from '../../../components/spinner/spinner';
import FileSaver from 'file-saver';
import { DateTime } from 'luxon';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { SelectForm } from '../../../components/select-form/select-form';
import { Category } from '../../../clients/quipli-service/category/category';
import { QuipliLink } from '../../../components/link/quipli-link';
import { useCategoryApiV1 } from '../../../clients/quipli-service/category/category-api-v1';
import { Typography } from '../../../components/typography/typography';
import { PaginationData } from '../../../clients/quipli-service/pagination/pagination';
import { SearchBar } from '../../../components/search-bar/search-bar';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { FiltersModal } from '../../../components/modal/filters-modal';
import { Card } from '../../../components/card/card';
import { useReportApiV2 } from '../../../clients/quipli-service/report/report-api-v2';
import {
  ProductReportRequest,
  ProductReportRow,
  ProductReportSummary,
} from '../../../clients/quipli-service/report/report';
import { displayMoneyFormat } from '../../../util/util';
import { Paginator } from '../../../components/paginator/paginator-v2';
import { Currency } from 'dinero.js';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { useSearchParams } from 'react-router-dom';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import { Badge } from '../../../components/badge/badge';
import { Location } from '../../../clients/quipli-service/location/location';
import { OrderType } from '../../../clients/quipli-service/order/order';
import { toast } from '../../../util/toasts';
import { SpinnerModal } from '../../../components/modal/spinner-modal';

export const ProductsReport = () => {
  const { productReport, productSummary } = useReportApiV2();
  const { getMultipleConfigurations } = useAPI();

  const searchRef = useRef<any>(null);

  const [currency, setCurrency] = useState<Currency>('USD');
  const [depreciationEnabled, setDepreciationEnabled] = useState<boolean>(false);
  const [items, setItems] = useState<ProductReportRow[]>([]);
  const [summary, setSummary] = useState<ProductReportSummary | undefined>();
  const [pagination, setPagination] = useState<PaginationData>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  // List searchRequest hooks
  const [params] = useSearchParams();
  const {
    request,
    setRequest,
    numOfParams: activeFilters,
  } = useRequestQueryParams<ProductReportRequest>(
    {
      keyword: params.get('keyword') ? params.get('keyword')! : undefined,
      from: params.get('from') ? DateTime.fromISO(params.get('from')!, { setZone: true }) : undefined,
      to: params.get('to') ? DateTime.fromISO(params.get('to')!, { setZone: true }) : undefined,
      categoryId: params.get('categoryId') ? parseInt(params.get('categoryId')!) : undefined,
      locationId: params.get('locationId') ? parseInt(params.get('locationId')!) : undefined,
      productFor: params.get('productFor')
        ? params.get('productFor')! === 'Sale'
          ? OrderType.Sale
          : OrderType.Rental
        : undefined,
      trackingType: params.get('trackingType') ? (params.get('trackingType')! as ProductTrackingType) : undefined,
      createdFrom: params.get('createdFrom') ? DateTime.fromISO(params.get('createdFrom')!) : undefined,
      createdTo: params.get('createdTo') ? DateTime.fromISO(params.get('createdTo')!) : undefined,
      pageSize: params.get('pageSize') ? parseInt(params.get('pageSize')!) : undefined,
      pageNumber: params.get('pageNumber') ? parseInt(params.get('pageNumber')!) : 1,
    },
    async (_request) => {
      setIsLoading(true);
      setSummary(undefined);
      setItems([]);
      const {
        keyword,
        from,
        to,
        trackingType,
        categoryId,
        locationId,
        productFor,
        createdFrom,
        createdTo,
        pageSize,
        pageNumber,
      } = _request;

      const [{ items, pagination }, reportSummary] = await Promise.all([
        productReport({
          from,
          to,
          keyword,
          trackingType,
          categoryId,
          locationId,
          productFor,
          createdFrom,
          createdTo,
          pageSize,
          pageNumber,
          includeSoldUnits: true,
        }),
        productSummary({
          from,
          to,
          keyword,
          trackingType,
          categoryId,
          locationId,
          productFor,
          createdFrom,
          createdTo,
        }),
      ]);

      setItems(items);
      setPagination(pagination);
      setSummary(reportSummary);
      setIsLoading(false);
    },
    [currency],
  );

  useEffect(() => {
    const init = async () => {
      const configs = await getMultipleConfigurations(['CONF_CURRENCY', 'CONF_PRODUCT_DEPRECIATION_ENABLED']);
      const currencyConfig = configs.find((c) => c.variable === 'CONF_CURRENCY')?.value;
      const depreciation = configs.find((c) => c.variable === 'CONF_PRODUCT_DEPRECIATION_ENABLED')?.value;
      if (currencyConfig) {
        setCurrency(currencyConfig as Currency);
      }
      setDepreciationEnabled(depreciation === '1');
    };

    init();
  }, []);

  return (
    <div className="px-[10px] pb-[5px] flex flex-col gap-[24px] w-full">
      <Typography type="display-sm" weight="medium">
        Product Report
      </Typography>

      <div className="flex flex-wrap justify-between gap-2 items-start">
        <div className="order-2 md:order-1 flex gap-2">
          <div className="flex gap-2 flex-wrap">
            <SearchBar
              ref={searchRef}
              data-cy="search-product-report"
              placeholder="Search by product name"
              defaultValue={request.keyword}
              setRequest={setRequest as any}
            />
            <ProductsReportFilters
              searchRef={searchRef}
              request={request}
              setRequest={setRequest}
              activeFilters={activeFilters}
            />
          </div>
        </div>

        <div className="order-1 md:order2 flex gap-2">
          <ExportButton request={request} setIsExporting={setIsExporting} />
        </div>
      </div>

      <SummaryStats summary={summary} currency={currency} />

      <div className={styles.cardTable}>
        <div className={styles.reportTableWrapper}>
          <table className={classNames(styles.reportTable)}>
            <thead>
              <tr>
                <th>
                  <Typography type="text-sm" className="text-grey-500">
                    Product Name
                  </Typography>
                </th>
                <th>
                  <Typography type="text-sm" className="text-grey-500">
                    Tracking Type
                  </Typography>
                </th>
                <th>
                  <Typography type="text-sm" className="text-grey-500">
                    Purchase Price
                  </Typography>
                </th>
                <th>
                  <Typography type="text-sm" className="text-grey-500">
                    Location
                  </Typography>
                </th>
                <th>
                  <Typography type="text-sm" className="text-grey-500">
                    Total Qty/Units
                  </Typography>
                </th>
                <th>
                  <Typography type="text-sm" className="text-grey-500">
                    No. of Orders
                  </Typography>
                </th>
                <th>
                  <Typography type="text-sm" className="text-grey-500">
                    Sold/Rented Qty.
                  </Typography>
                </th>
                <th>
                  <Typography type="text-sm" className="text-grey-500">
                    Days On Rent
                  </Typography>
                </th>
                <th>
                  <Typography type="text-sm" className="text-grey-500">
                    Rental Revenue
                  </Typography>
                </th>
                <th>
                  <Typography type="text-sm" className="text-grey-500">
                    Sale Revenue
                  </Typography>
                </th>
                <th>
                  <Typography type="text-sm" className="text-grey-500">
                    Physical Utilization
                  </Typography>
                </th>
                <th>
                  <Typography type="text-sm" className="text-grey-500">
                    Dollar Utilization
                  </Typography>
                </th>
                <th>
                  <Typography type="text-sm" className="text-grey-500">
                    Combined Utilization
                  </Typography>
                </th>
                <th>
                  <Typography type="text-sm" className="text-grey-500">
                    Revenue ROI
                  </Typography>
                </th>
                <th>
                  <Typography type="text-sm" className="text-grey-500">
                    ARPU
                  </Typography>
                </th>
                <th>
                  <Typography type="text-sm" className="text-grey-500">
                    Average Daily Rental Rate
                  </Typography>
                </th>
                <th>
                  <Typography type="text-sm" className="text-grey-500">
                    Service Costs
                  </Typography>
                </th>
                <th>
                  <Typography type="text-sm" className="text-grey-500">
                    Hard Days Down
                  </Typography>
                </th>
                <th>
                  <Typography type="text-sm" className="text-grey-500">
                    Soft Days Down
                  </Typography>
                </th>
                <th>
                  <Typography type="text-sm" className="text-grey-500">
                    Number of Work Orders
                  </Typography>
                </th>
                {depreciationEnabled && (
                  <th>
                    <Typography type="text-sm" className="text-grey-500">
                      Depreciated Value
                    </Typography>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={100}>
                    <Spinner />
                  </td>
                </tr>
              ) : (
                <>
                  {items && (
                    <>
                      {items.length > 0 ? (
                        items.map((report, i) => (
                          <TableRow
                            key={i}
                            row={report}
                            currency={currency}
                            depreciationEnabled={depreciationEnabled}
                            isParent
                          />
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7}>No record found</td>
                        </tr>
                      )}
                    </>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
        <Paginator
          request={request}
          pagination={pagination}
          setRequest={(paginator) => setRequest((prev) => ({ ...prev, ...paginator }))}
        />
      </div>
      <SpinnerModal isOpen={isExporting} contentDescription={'Exporting Products Report'} />
    </div>
  );
};

const ProductsReportFilters = ({
  searchRef,
  request,
  setRequest,
  activeFilters,
}: {
  searchRef: React.MutableRefObject<any>;
  request: ProductReportRequest;
  setRequest: React.Dispatch<React.SetStateAction<ProductReportRequest>>;
  activeFilters: number;
}) => {
  const { listCategories } = useCategoryApiV1();
  const { listLocations } = useAPI();

  // React states
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [trackingType, setTrackingType] = useState<{ label: string; value: string } | null>(
    request.trackingType ? { label: request.trackingType, value: request.trackingType } : null,
  );
  const [category, setCategory] = useState<Category | null>(null);
  const [location, setLocation] = useState<Location | null>(null);
  const [productFor, setProductFor] = useState<{ label: string; value: OrderType } | null>(
    request.productFor ? { label: request.productFor, value: request.productFor } : null,
  );
  const [fromDate, setFromDate] = useState<DateTime>();
  const [toDate, setToDate] = useState<DateTime>();
  const [productFromDate, setProductFromDate] = useState<DateTime | undefined>(request.createdFrom);
  const [productToDate, setProductToDate] = useState<DateTime | undefined>(request.createdTo);

  const trackingTypeValues = Object.keys(ProductTrackingType).map((key) => ({ label: key, value: key }));
  const productForValues: { label: string; value: OrderType }[] = [
    { label: 'Sale', value: OrderType.Sale },
    { label: 'Rent', value: OrderType.Rental },
  ];

  // Callbakcs
  const search = () => {
    const timezone = location?.timezone || locations.find(() => true)?.timezone;

    setRequest(({ keyword, pageSize }) => ({
      keyword,
      trackingType: ProductTrackingType[trackingType?.value as keyof typeof ProductTrackingType],
      categoryId: category?.id,
      locationId: location?.id,
      productFor: productFor?.value,
      from: fromDate?.setZone(timezone, { keepLocalTime: true }),
      to: toDate?.setZone(timezone, { keepLocalTime: true }),
      createdFrom: productFromDate,
      createdTo: productToDate,
      pageNumber: 1,
      pageSize,
    }));
  };

  const clear = () => {
    setTrackingType(null);
    setCategory(null);
    setLocation(null);
    setProductFor(null);
    setFromDate(undefined);
    setToDate(undefined);
    setProductFromDate(undefined);
    setProductToDate(undefined);

    if (searchRef.current?.setKeyword) {
      searchRef.current.setKeyword('');
    }

    setRequest(({ pageSize }) => ({
      pageNumber: 1,
      pageSize,
    }));
  };

  useEffect(() => {
    const fetch = async () => {
      const _categories = await listCategories();
      setCategories(_categories.filter((c) => c.isActive && !c.isDeleted));
      setCategory(_categories.find((c) => c.id === request.categoryId) ?? null);

      const _locations = await listLocations();
      setLocations(_locations);
      setLocation(_locations.find((l) => l.id === request.locationId) ?? null);
    };

    fetch();
  }, []);

  return (
    <>
      <Button className="button-secondary !border-0 h-[46px] relative" onClick={() => setIsModalOpen(true)}>
        <Typography type="text-md" className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]">
          <Icon icon="FilterLines" stroke="var(--grey-700)" /> Filters
        </Typography>

        {activeFilters > 0 && (
          <Badge className="absolute -right-2 -top-2" text={`${activeFilters}`} size="small" type="yellow" />
        )}
      </Button>

      {activeFilters > 0 && (
        <Button className="button-secondary !border-0 h-[46px]" onClick={() => clear()}>
          <Typography type="text-md" className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]">
            Clear Filters
          </Typography>
        </Button>
      )}

      <FiltersModal
        isOpen={isModalOpen}
        title="Filters"
        onConfirm={() => {
          setIsModalOpen(false);
          search();
        }}
        onCancel={() => {
          setIsModalOpen(false);
        }}
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <Typography type="text-md" weight="medium">
              Category
            </Typography>
            <SelectForm
              value={category}
              options={categories}
              onChange={(c) => setCategory(c)}
              getOptionLabel={(c) => c.name}
              getOptionValue={(c) => `${c.id}`}
              isClearable
            />
          </div>

          <div className="flex flex-col gap-2">
            <Typography type="text-md" weight="medium">
              Location
            </Typography>
            <SelectForm
              value={location}
              options={locations}
              onChange={(l) => setLocation(l)}
              getOptionLabel={(l) => l.name}
              getOptionValue={(l) => `${l.id}`}
              isClearable
            />
          </div>

          <div className="flex flex-col gap-2">
            <Typography type="text-md" weight="medium">
              Product For
            </Typography>
            <SelectForm
              options={productForValues}
              value={productFor}
              onChange={(pf) => setProductFor(pf)}
              isSearchable={false}
              isClearable
            />
          </div>

          <div className="flex flex-col gap-2">
            <Typography type="text-md" weight="medium">
              Tracking Type
            </Typography>
            <SelectForm
              options={trackingTypeValues}
              value={trackingType}
              onChange={(tp) => setTrackingType(tp)}
              isSearchable={false}
              isClearable
            />
          </div>

          <div className="flex flex-col gap-2">
            <Typography type="text-md" weight="medium">
              Order Date
            </Typography>
            <div className="flex flex-col md:flex-row gap-2">
              <DatePicker
                value={fromDate || DateTime.fromFormat(`01-01-${DateTime.local().year}`, 'dd-MM-yyyy')}
                onChange={(date) => setFromDate(date!)}
              />
              <DatePicker
                value={toDate || DateTime.fromFormat(DateTime.local().toFormat('dd-MM-yy'), 'dd-MM-yy')}
                onChange={(date) => setToDate(date!)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Typography type="text-md" weight="medium">
              Product Created
            </Typography>
            <div className="flex flex-col md:flex-row gap-2">
              <DatePicker value={productFromDate} onChange={(date) => setProductFromDate(date)} />
              <DatePicker value={productToDate} onChange={(date) => setProductToDate(date)} />
            </div>
          </div>
        </div>
      </FiltersModal>
    </>
  );
};

const ExportButton = ({
  request,
  setIsExporting,
}: {
  request: ProductReportRequest;
  setIsExporting: (isExporting: boolean) => void;
}) => {
  const { exportProductReport } = useReportApiV2();

  // Callbacks
  const exportToCSV = async () => {
    setIsExporting(true);
    try {
      const file = await exportProductReport({
        ...request,
        pageNumber: undefined,
        pageSize: undefined,
      });

      const exportFileName = `Products_Report_${DateTime.local().toFormat('D').replaceAll('/', '-')}`;
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const fileExtension = '.xlsx';

      const data = new Blob([file], { type: fileType });
      FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
    } catch (e: any) {
      toast('Something went wrong while exporting the report', 'error');
      console.error(e);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <Button onClick={exportToCSV} className="button-yellow h-[46px] flex justify-between" data-cy="export">
        <Typography type="text-md" weight="semi-bold">
          Export
        </Typography>
        <Icon icon="Export" stroke="var(--quipli-black-800)" />
      </Button>
    </div>
  );
};

const SummaryStats = ({ summary, currency }: { summary?: ProductReportSummary; currency: Currency }) => {
  const SummaryBox = ({
    title,
    onRentPercentage,
    revenue,
    arpu,
    averageDailyRentalRate,
  }: {
    title?: string;
    onRentPercentage: string;
    revenue: number;
    arpu: number;
    averageDailyRentalRate: number;
  }) => {
    return (
      <Card className="m-[2px] p-[12px] min-h-[64px] rounded-md flex flex-col gap-2 grow-0 shrink basis-[calc(100%-4px)] md:basis-[calc(50%-4px)] lg:basis-[calc(20%-4px)]">
        {title && (
          <Typography type="text-sm" weight="regular" className="text-grey-700">
            {title}
          </Typography>
        )}

        <div>
          <div className="flex items-center justify-between gap-2">
            <Typography type="text-sm" weight="regular" className="text-grey-500">
              Physical Utilization
            </Typography>
            <Typography type="text-sm" weight="medium" className="text-[var(--quipli-black-800)]">
              {onRentPercentage}
            </Typography>
          </div>
          <div className="flex items-center justify-between gap-2">
            <Typography type="text-sm" weight="regular" className="text-grey-500">
              Revenue
            </Typography>
            <Typography type="text-sm" weight="medium" className="text-[var(--quipli-black-800)]">
              {displayMoneyFormat(revenue, currency)}
            </Typography>
          </div>
          <div className="flex items-center justify-between gap-2">
            <Typography type="text-sm" weight="regular" className="text-grey-500">
              ARPU
            </Typography>
            <Typography type="text-sm" weight="medium" className="text-[var(--quipli-black-800)]">
              {displayMoneyFormat(arpu, currency)}
            </Typography>
          </div>
          <div className="flex items-center justify-between gap-2">
            <Typography type="text-sm" weight="regular" className="text-grey-500">
              Average Daily Rental Rate
            </Typography>
            <Typography type="text-sm" weight="medium" className="text-[var(--quipli-black-800)]">
              {displayMoneyFormat(averageDailyRentalRate, currency)}
            </Typography>
          </div>
        </div>
      </Card>
    );
  };

  const SummaryWrapper = ({ title, children }: PropsWithChildren<{ title: string }>) => {
    return (
      <div className="flex flex-col gap-4">
        <Typography type="text-md" weight="medium">
          {title}
        </Typography>

        <div className="flex w-full flex-wrap gap-1">{children}</div>
      </div>
    );
  };

  return summary ? (
    <div className="flex flex-col gap-8">
      {summary.overall !== undefined && (
        <SummaryWrapper title="Overall">
          <SummaryBox
            onRentPercentage={summary.overall.onRentPercentage}
            revenue={summary.overall.revenue}
            arpu={summary.overall.arpu}
            averageDailyRentalRate={summary.overall.averageDailyRentalRate}
          />
        </SummaryWrapper>
      )}
      {summary.locations !== undefined && (
        <SummaryWrapper title="Locations">
          {summary.locations.map((location, i) => (
            <SummaryBox
              key={`summary-location-${i}`}
              title={location.locationName}
              onRentPercentage={location.onRentPercentage}
              revenue={location.revenue}
              arpu={location.arpu}
              averageDailyRentalRate={location.averageDailyRentalRate}
            />
          ))}
        </SummaryWrapper>
      )}
      {summary.categories.length > 0 && (
        <SummaryWrapper title="Categories">
          {summary.categories.map((category, i) => (
            <SummaryBox
              key={`summary-category-${i}`}
              title={category.categoryName}
              onRentPercentage={category.onRentPercentage}
              revenue={category.revenue}
              arpu={category.arpu}
              averageDailyRentalRate={category.averageDailyRentalRate}
            />
          ))}
        </SummaryWrapper>
      )}
    </div>
  ) : null;
};

const TableRow = ({
  row,
  currency,
  depreciationEnabled,
  isParent,
  isParentExpanded,
}: {
  row: ProductReportRow;
  currency: Currency;
  depreciationEnabled: boolean;
  isParent: boolean;
  isParentExpanded?: boolean;
}) => {
  const hasChildren = isParent && row.Units && row.Units.length > 0;
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  return (
    <>
      <tr
        className={classNames({
          'bg-grey-100': !isParent,
          hidden: isParentExpanded === false,
        })}
      >
        <td>
          <div className="flex justify-between items-center flex-nowrap">
            {isParent ? (
              <>
                <QuipliLink
                  to={`products/form/${row.ID}`}
                  className="underline underline-offset-1 text-quipli-black-800"
                >
                  <Typography type="text-sm">{row.Product}</Typography>
                </QuipliLink>

                {hasChildren && (
                  <Button className="button-tertiary cursor-pointer" onClick={() => setIsExpanded((prev) => !prev)}>
                    <Icon icon={isExpanded ? 'ChevronUp' : 'ChevronDown'} className="text-grey-500" />
                  </Button>
                )}
              </>
            ) : (
              <Typography type="text-sm" className="text-quipli-black-800">
                {row.Product}
              </Typography>
            )}
          </div>
        </td>
        <td>
          <Typography type="text-sm" className="text-quipli-black-800">
            {row['Tracking Type']}
          </Typography>
        </td>
        <td>
          <Typography type="text-sm" className="text-quipli-black-800">
            {displayMoneyFormat(row['Purchase Price'], currency)}
          </Typography>
        </td>
        <td>
          <Typography type="text-sm" className="text-quipli-black-800">
            {row['Location']}
          </Typography>
        </td>
        <td>
          <Typography type="text-sm" className="text-quipli-black-800">
            {row['Total Qty/Units']}
          </Typography>
        </td>
        <td>
          <Typography type="text-sm" className="text-quipli-black-800">
            {row['No. Of Orders']}
          </Typography>
        </td>
        <td>
          <Typography type="text-sm" className="text-quipli-black-800">
            {row['Sold/Rented Qty']}
          </Typography>
        </td>
        <td>
          <Typography type="text-sm" className="text-quipli-black-800">
            {row['Days On Rent']}
          </Typography>
        </td>
        <td>
          <Typography type="text-sm" className="text-quipli-black-800">
            {displayMoneyFormat(row['Rental Revenue'], currency)}
          </Typography>
        </td>
        <td>
          <Typography type="text-sm" className="text-quipli-black-800">
            {displayMoneyFormat(row['Sale Revenue'], currency)}
          </Typography>
        </td>
        <td>
          <Typography type="text-sm" className="text-quipli-black-800">
            {row['Physical Utilization']}
          </Typography>
        </td>
        <td>
          <Typography type="text-sm" className="text-quipli-black-800">
            {row['Dollar Utilization']}
          </Typography>
        </td>
        <td>
          <Typography type="text-sm" className="text-quipli-black-800">
            {row['Combined Utilization']}
          </Typography>
        </td>
        <td>
          <Typography type="text-sm" className="text-quipli-black-800">
            {row['Revenue ROI']}
          </Typography>
        </td>
        <td>
          <Typography type="text-sm" className="text-quipli-black-800">
            {displayMoneyFormat(row['ARPU'], currency)}
          </Typography>
        </td>
        <td>
          <Typography type="text-sm" className="text-quipli-black-800">
            {displayMoneyFormat(row['Average Daily Rental Rate'], currency)}
          </Typography>
        </td>
        <td>
          <Typography type="text-sm" className="text-quipli-black-800">
            {displayMoneyFormat(row['Service Costs'], currency)}
          </Typography>
        </td>
        <td>
          <Typography type="text-sm" className="text-quipli-black-800">
            {row['Hard Days Down']}
          </Typography>
        </td>
        <td>
          <Typography type="text-sm" className="text-quipli-black-800">
            {row['Soft Days Down']}
          </Typography>
        </td>
        <td>
          <Typography type="text-sm" className="text-quipli-black-800">
            {row['Number of Work Orders']}
          </Typography>
        </td>
        {depreciationEnabled && (
          <td>
            <Typography type="text-sm" className="text-quipli-black-800">
              {row['Depreciated Value'] !== undefined
                ? displayMoneyFormat(row['Depreciated Value'], currency)
                : undefined}
            </Typography>
          </td>
        )}
      </tr>

      {hasChildren &&
        row.Units?.map((unitReport) => (
          <TableRow
            key={`${row.Product}_${unitReport.Product}`}
            row={unitReport}
            currency={currency}
            depreciationEnabled={depreciationEnabled}
            isParent={false}
            isParentExpanded={isExpanded}
          />
        ))}
    </>
  );
};
